import React, { useCallback, useEffect, useState } from "react";
import TableWrapper from "components/common/table-wrapper";
import DynamicTable from "components/common/dynamic-table";
import ApprovalStepper from "components/common/approval-stepper/ApprovalStepper";
import { HOUSING_EXPENSE_CREATE_REQUEST_PATH } from "constants/routePaths";
import debounce from "lodash/debounce";
import {
  TrashSimple,
  Plus,
  Pencil,
  Eye,
  DownloadSimple,
  X,
  Check as CheckIcon,
  Funnel,
  CurrencyDollar,
} from "phosphor-react";
import {
  useSearchParams,
  useNavigate,
  createSearchParams,
} from "react-router-dom/dist";
import {
  highlightFirstMatch,
  getUserRole,
  getUserInfo,
  canApproveRejectExpenseRequests,
  canCreateExpenseRequests,
  canConfirmPaymentIssuance,
  capitalizeName,
  formatDate,
  DateFormat,
} from "utils/common";
import CustomModal from "components/common/modal";
import { DEBOUNCE_DELAY, statusBadge } from "constants/common";
import Check from "components/common/check";
import PaymentRejectForm from "./modals/PaymentRejectForm";
import PaymentForm from "./modals/PaymentForm";
import HousingExpenseRequestFilter from "./HousingExpenseRequestFilter";
import AddExpenseWithMember from "./AddExpenseWithMember";
import {
  ApprovedHousingExpense,
  getHousingExpense,
  getHousingExpenseById,
  RejectedHousingExpense,
} from "containers/housing-expense-requests/api";
import CreateHousingExpenseForm from "components/housing-expense-request/CreateForm";
import HousingPaymentForm from "./modals/HousingPaymentForm";
// import { set } from "lodash";
// import AddFormWithoutMember from "./AddFormWithoutMember";

const initialMetaForTable = {
  search: "",
  status: "show_all_requests",
  perPage: "50",
  mif_status: "active",
};

let timeOut;

const HousingExpenseRequestComp = () => {
  const [housingExpenses, setHousingExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [meta, setMeta] = useState({
    ...initialMetaForTable,
    mif_status: "active",
    perPage: "20",
  });
  const [searchParams] = useSearchParams();
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState("");
  // const [addNewMemberModal, setAddNewMemberModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedRecord, setSelectedRecord] = useState("");
  const [viewRecord, setViewRecord] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isRejectedPaymentVisible, setIsRejectedPaymentVisible] = useState(
    false,
  );
  const [isAcceptPaymentVisible, setIsAcceptPaymentVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [refreshPage, setRefreshPage] = useState(false);
  const [
    FilterHousingExpenseRequests,
    setFilterHousingExpenseRequests,
  ] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filters, setFilters] = useState({
    state: null,
    county: null,
    health_plan_id: null,
    expense_request_status: ["draft", "pending", "approved"],
  });

  // Get user role and permissions
  const userInfo = getUserInfo();
  const currentUserId = userInfo?.id;
  const userRole = getUserRole();
  const canApproveReject = canApproveRejectExpenseRequests(userRole);
  const canCreate = canCreateExpenseRequests(userRole);
  const canConfirmPayment = canConfirmPaymentIssuance(userRole);
  const isExecutive = userRole === "Executive";
  const isAccounting = userRole === "Accounting";
  const isProgramCoordinator = userRole === "ProgramCoordinator";
  const isHousingNavigator = userRole === "HousingNavigator";
  const isCsManager = userRole === "CsManager";

  const getStatusConfig = (status) => {
    const normalized = status
      ?.toString()
      .trim()
      .toLowerCase();
    const map = {
      draft: { label: "Draft", class: "draft" },
      submitted: { label: "Submitted", class: "submitted-housing" },
      pending: { label: "Pending", class: "pending" },
      "housing-pending": { label: "Pending", class: "pending" },
      approved: { label: "Approved", class: "approved" },
      completed: { label: "Completed", class: "completed" },
      cancelled: { label: "Cancelled", class: "cancelled" },
      denied: { label: "Denied", class: "denied" },
    };
    return map[normalized] || { label: status || "—", class: "unknown" };
  };
  const columns = [
    {
      columnKey: "id",
      label: "ID",
      isSortable: true,
      render: (value) => highlightFirstMatch(String(value), meta.search),
    },
    {
      columnKey: `member.first_name member.last_name`,
      label: "Member Name",
      isSortable: true,
      render: (value, record) => {
        let name;

        if (value) {
          name = value;
        } else {
          const { first_name, last_name } = record?.member || {};
          if (first_name && last_name) name = `${first_name} ${last_name}`;
          else if (first_name) name = first_name;
          else if (last_name) name = last_name;
          else return "—";
        }

        return highlightFirstMatch(name, meta.search); // <-- FIX
      },
    },

    {
      columnKey: "member.cin",
      label: "CIN",
      isSortable: true,
      render: (value) => highlightFirstMatch(value, meta.search),
    },
    // {
    //   columnKey: "member.addresses",
    //   label: "Address",
    //   isSortable: false,
    //   render: (value, record) => {
    //     const addresses = Array.isArray(record?.member?.addresses)
    //       ? record.member.addresses
    //       : [];
    //     const home = addresses.find((a) => a?.address_type === "home");
    //     const fullAddress = home?.full_address || "—";
    //     return (
    //       <div
    //         title={fullAddress}
    //         style={{
    //           whiteSpace: "nowrap",
    //           overflow: "hidden",
    //           textOverflow: "ellipsis",
    //           maxWidth: "300px",
    //         }}
    //       >
    //         {fullAddress}
    //       </div>
    //     );
    //   },
    // },
    {
      columnKey: "total_expense",
      label: "Total Expense",
      isSortable: true,
      render: (value) => (value ? `$${value}` : ""),
    },
    {
      columnKey: "next_approver",
      label: "Next Approver",
      isSortable: true,
      render: (value) => (value ? `${value}` : ""),
    },
    {
      columnKey: "created_at",
      label: "Created at",
      isSortable: true,
      render: (value) => {
        if (!value) return "—";
        const date = new Date(value);

        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours === 0 ? 12 : hours; // 12-hour format
        const formattedHours = String(hours).padStart(2, "0");

        return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
      },
    },

    {
      columnKey: "status",
      label: "Status",
      isSortable: false,
      render: (value) => {
        const config = getStatusConfig(value);
        return <div className={`status ${config.class}`}>{config.label}</div>;
      },
    },
  ];
  const handleRefresh = () => {
    setRefresh(!refresh);
  };

  const fetchHousingExpenseById = async (id) => {
    setDetailLoading(true);
    try {
      const data = await getHousingExpenseById(id);
      return data;
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchHousingExpense = async () => {
    setLoading(true);
    try {
      const result = await getHousingExpense({
        ...meta,
        ...filters,
        page: searchParams.get("page") ? searchParams.get("page") : 1,
        key: sortConfig.key,
        order: sortConfig.direction,
      });
      if (result["housing_expenses"]) {
        const data = result?.housing_expenses.map((item) => {
          const totalExpense = Array.isArray(item.expense_informations)
            ? item.expense_informations.reduce(
                (sum, exp) => sum + parseFloat(exp.expense_amount || 0),
                0,
              )
            : 0;

          return {
            ...item,
            total_expense: totalExpense.toFixed(2),
          };
        });

        setHousingExpenses(data);
        setTotalCount(result?.meta?.total_count);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = (callback, delay) => {
    clearTimeout(timeOut);
    timeOut = setTimeout(callback, delay);
  };

  const handleSetSearchQuery = (value) => {
    setMeta((prev) => ({ ...prev, search: value }));
    debounceFn(handleRefreshPage, DEBOUNCE_DELAY);
  };

  const handleRefreshPage = () => {
    setRefreshPage((prev) => !prev);
  };

  useEffect(() => {
    fetchHousingExpense();
  }, [refreshPage, searchParams, sortConfig, refresh, filters]);

  const handleOpenFilterHousingExpenseRequest = () => {
    setFilterHousingExpenseRequests(true);
  };
  const handleCloseFilterHousingExpenseRequest = () => {
    setFilterHousingExpenseRequests(false);
  };
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    handleCloseFilterHousingExpenseRequest();
  };

  const RejectHousingPaymentSubmission = async (id, data) => {
    setLoading(true);
    try {
      const response = await RejectedHousingExpense(id, data);
      if (response) {
        handleCloseRejectPayment();
        handleRefresh();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const ApprovedHousingPaymentSubmission = async (id, data) => {
    setLoading(true);
    try {
      const response = await ApprovedHousingExpense(id, data);
      if (response) {
        handleCloseAcceptPayment();
        handleRefresh();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const actionButtons = [
    {
      label: "Filter Housing Expense",
      classes: "primary-btn",
      icon: <Funnel size={24} className="me-3" />,
      handleClick: handleOpenFilterHousingExpenseRequest,
    },
    ...(canCreate
      ? [
          {
            label: "Create Request",
            classes: "primary-btn",
            icon: <Plus size={24} className="me-3" />,
            handleClick: () => setIsExpenseModalOpen(true),
          },
        ]
      : []),
  ];
  const handleOpenViewRecord = (row) => {
    setSelectedRecord(row);
    setViewRecord(true);
  };
  const handleCloseViewRecord = () => {
    setSelectedRecord(null);
    setViewRecord(false);
  };
  const handleVisibleRejectPayment = (row) => {
    setIsRejectedPaymentVisible(true);
    setSelectedId(row);
  };
  const handleCloseRejectPayment = () => {
    setIsRejectedPaymentVisible(false);
    setSelectedId("");
  };
  const handleVisibleAcceptPayment = (row) => {
    setIsAcceptPaymentVisible(true);
    setSelectedId(row);
  };
  const handleCloseAcceptPayment = () => {
    setIsAcceptPaymentVisible(false);
    setSelectedId("");
  };
  const handleExpenseModalClose = () => {
    setIsExpenseModalOpen(false);
    setSelectedExpense("");
  };

  /**
   * Determines which action icons should be visible for a given expense request row
   * based on user role, status, and pending approval state
   */
  const getVisibleIcons = (row) => {
    const status = row?.status?.toLowerCase() || "";
    const createdByUserId =
      row?.created_by_user_id || row?.user_id || row?.created_by_id;
    // Check multiple possible field names for pending approval user
    const pendingApprovalUserId =
      row?.pending_approval_user_id ||
      row?.pending_approval_user?.id ||
      row?.pending_user_id ||
      row?.pending_approver_id ||
      null;
    // Check multiple possible field names for pending approval role
    const pendingApprovalRole =
      row?.pending_approval_role ||
      row?.pending_approval_user?.type ||
      row?.pending_role ||
      null;
    const icons = [];

    // Helper to compare user IDs (handle string/number mismatch)
    const isPendingForCurrentUser = () => {
      if (!pendingApprovalUserId || !currentUserId) return false;
      // Convert both to strings for comparison to handle type mismatches
      return String(pendingApprovalUserId) === String(currentUserId);
    };

    // Helper to compare user IDs (handle string/number mismatch)
    const isCreatedByCurrentUser = () => {
      if (!createdByUserId || !currentUserId) return false;
      return String(createdByUserId) === String(currentUserId);
    };

    // Debug logging for Accounting users with pending status (remove in production)
    if (isAccounting && status === "pending") {
      console.log("🔍 Accounting user - Pending expense debug:", {
        status,
        currentUserId,
        currentUserRole: userRole,
        pendingApprovalUserId,
        pendingApprovalRole,
        rowId: row?.id,
        availableFields: Object.keys(row).filter(
          (key) =>
            key.toLowerCase().includes("pending") ||
            key.toLowerCase().includes("approval") ||
            key.toLowerCase().includes("user") ||
            key.toLowerCase().includes("role"),
        ),
        isPendingForUser: isPendingForCurrentUser(),
        showApproveDeny: status === "pending" && isPendingForCurrentUser(),
      });
    }

    // VIEW icon visibility rules
    const showView = () => {
      if (status === "draft") {
        // Draft: Only for creator
        return isCreatedByCurrentUser();
      }
      // All other statuses: Show for all users
      return true;
    };

    // EDIT icon visibility rules
    const showEdit = () => {
      // Executive and Accounting never see edit
      if (isExecutive || isAccounting) return false;

      // Status-based rules
      if (status === "draft") {
        // Draft: Only for creator (any role that can create)
        return isCreatedByCurrentUser();
      }
      if (status === "submitted") {
        // Submitted: Program Coordinators, Housing Navigators, CS Managers
        return isProgramCoordinator || isHousingNavigator || isCsManager;
      }
      if (status === "pending") {
        // Pending: Hidden from all
        return false;
      }
      if (status === "approved") {
        // Approved: Program Coordinators, Housing Navigators, CS Managers
        return isProgramCoordinator || isHousingNavigator || isCsManager;
      }
      if (status === "completed") {
        // Completed: Hidden from all
        return false;
      }
      if (status === "cancelled") {
        // Cancelled: Program Coordinators, Housing Navigators, CS Managers
        return isProgramCoordinator || isHousingNavigator || isCsManager;
      }
      if (status === "denied") {
        // Denied: Program Coordinators, Housing Navigators, CS Managers
        return isProgramCoordinator || isHousingNavigator || isCsManager;
      }
      return false;
    };

    // APPROVE/DENY icon visibility rules
    const showApproveDeny = () => {
      const nextApprover = row?.next_approver?.trim();

      const loggedInEmail = userInfo?.email?.trim().toLowerCase();
      const approvalConfig =
        row?.expense_informations?.[0]?.approval_configuration;
      const executiveEmail = approvalConfig?.executive_email
        ?.trim()
        .toLowerCase();
      const accountingEmail = approvalConfig?.accounting_approval_email
        ?.trim()
        .toLowerCase();

      const roleMap = {
        Executive: isExecutive,
        Accounting: isAccounting,
        CsManager: isCsManager,
        ProgramCoordinator: isProgramCoordinator,
      };

      if (!roleMap[nextApprover]) return false;
      if (!canApproveReject) return false;

      const validStatuses = ["submitted", "pending", "housing-pending"];
      if (!validStatuses.includes(status?.toLowerCase())) return false;

      if (nextApprover === "Executive") {
        return executiveEmail === loggedInEmail;
      }

      if (nextApprover === "Accounting") {
        return accountingEmail === loggedInEmail;
      }
      if (nextApprover === "CsManager") {
        if (row?.member?.case_manager_id === userInfo?.id) return true;
      }
      if (nextApprover === "ProgramCoordinator") {
        return true;
      }
      return isPendingForCurrentUser();
    };

    const showDollarSign = () => {
      if (!isAccounting) return false;
      if (row?.status?.toLowerCase() === "draft") return false;
      if (row?.is_payment_confirmed) return false;

      const nextApprover = row?.next_approver?.trim();
      const approvalConfig =
        row?.expense_informations?.[0]?.approval_configuration;
      const accountingEmail = approvalConfig?.accounting_approval_email
        ?.trim()
        .toLowerCase();
      const loggedInEmail = userInfo?.email?.trim().toLowerCase();
      if (accountingEmail !== loggedInEmail) return false;
      if (nextApprover === "Accounting") return false;
      return true;
    };

    // Build icons array in order: View, Edit, Reject, Approve, Dollar Sign
    if (showView()) icons.push(Eye);
    if (showEdit()) icons.push(Pencil);
    if (showApproveDeny()) {
      icons.push(X); // Reject
      icons.push(CheckIcon); // Approve
    }
    if (showDollarSign()) icons.push(CurrencyDollar);

    return icons;
  };

  /**
   * Maps icon index to action handler
   * Icons are returned in order: View, Edit, Reject, Approve, Dollar Sign
   */
  const handleIconClick = (iconIndex, row, icons) => {
    const iconAtPosition = icons[iconIndex];

    if (iconAtPosition === Eye) {
      const data = fetchHousingExpenseById(row.id);
      if (data) {
        handleOpenViewRecord(row);
      }
    } else if (iconAtPosition === Pencil) {
      const data = fetchHousingExpenseById(row.id);
      if (data) {
        setEditRecord(row);
        setIsEditModalVisible(true);
      }
    } else if (iconAtPosition === X) {
      handleVisibleRejectPayment(row);
    } else if (iconAtPosition === CheckIcon) {
      // This is the Approve action
      handleVisibleAcceptPayment(row);
    } else if (iconAtPosition === CurrencyDollar) {
      // This is the Confirm Payment action (Dollar Sign)
      handleVisibleAcceptPayment(row);
    }
  };
  const handlePageChange = (value) => {
    navigate({ search: createSearchParams({ page: value }).toString() });
    handleRefreshPage();
  };

  // const handleOpenAddNewMemberModal = () => {
  //   setAddNewMemberModal(true);
  // };
  // const handleCloseAddNewMemberModal = () => {
  //   setAddNewMemberModal(false);
  // };

  const showAddress = (value) => {
    return (
      <>
        {value?.street && (
          <>
            {capitalizeName(value.street)},
            <br />
          </>
        )}

        {(value?.city?.name || value?.state?.name) && (
          <>
            {capitalizeName(value?.city?.name)}
            {value?.city?.name && value?.state?.name && ", "}
            {capitalizeName(value?.state?.name)}
            {value?.zip_code?.name && `, ${value.zip_code.name}`}
            <br />
          </>
        )}

        {value?.county?.name && <>({capitalizeName(value.county.name)})</>}
      </>
    );
  };

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Member ID"
        setSearhQuery={(query) => {
          handleSetSearchQuery(query);
        }}
        searchValue={meta.search}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        setMeta={setMeta}
        currentPage={
          searchParams.get("page") ? parseInt(searchParams.get("page")) : 1
        }
        onPageChange={handlePageChange}
        isLoading={loading}
        actionButtons={actionButtons}
        enableSelection={false}
      >
        <DynamicTable
          rowKey="id"
          columns={columns}
          data={housingExpenses}
          expandable
          renderExpandedContent={(row) => (
            <ApprovalStepper key={row.id} housingExpense={row} />
          )}
          actionIcons={(row) => {
            return getVisibleIcons(row);
          }}
          onActionClick={(actionIndex, row) => {
            const icons = getVisibleIcons(row);
            handleIconClick(actionIndex, row, icons);
          }}
          searchValue={meta.search}
          enableSelection={true}
        />
      </TableWrapper>

      <CustomModal
        show={viewRecord && selectedRecord}
        onHide={handleCloseViewRecord}
        size="sm"
        heading={
          <div>
            View Request{" "}
            <span
              className={
                statusBadge[selectedRecord?.status?.toLowerCase()]?.className ||
                ""
              }
            >
              {statusBadge[selectedRecord?.status?.toLowerCase()]?.label ||
                selectedRecord?.status}
            </span>
          </div>
        }
      >
        <h4 className="my-4">Member Information</h4>
        <div className="row">
          <div className="col-sm-4">
            <h6 className="my-6">Member's Name</h6>
            <p className="heading-sm">
              {`${selectedRecord?.member?.first_name || ""} ${selectedRecord
                ?.member?.last_name || ""}`.trim() || ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">CIN</h6>
            <p className="heading-sm">{selectedRecord?.member?.cin || ""}</p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">Payment Method</h6>
            <p className="heading-sm">
              {selectedRecord?.expense_informations?.[0]?.approval_configuration
                ?.approval_method || ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">Address</h6>
            <p className="heading-sm">
              {showAddress(selectedRecord?.member?.addresses[1]) || ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">Expense ID</h6>
            <p className="heading-sm">
              {selectedRecord?.expense_informations?.[0]?.housing_expense_id ||
                ""}
            </p>
          </div>
        </div>
        <hr className="mt-4" />

        <h4 className="my-4">Documents</h4>
        <div className="row">
          <div className="col-sm-4">
            <h6 className="my-6">W-9</h6>
            {selectedRecord?.upload_w9 && (
              <div className="mt-2">
                <a
                  href={selectedRecord.upload_w9}
                  download={selectedRecord.upload_w9_file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedRecord.upload_w9_file_name}
                </a>
              </div>
            )}
          </div>

          <div className="col-sm-4">
            <h6 className="my-6">Lease Agreement</h6>
            {selectedRecord?.upload_lease_agreement && (
              <div className="mt-2">
                <a
                  href={selectedRecord.upload_lease_agreement}
                  download={selectedRecord.upload_lease_agreement_file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedRecord.upload_lease_agreement_file_name}
                </a>
              </div>
            )}
          </div>

          <div className="col-sm-4">
            <h6 className="my-6">Authorization from Health Plan</h6>
            {selectedRecord?.upload_authorization && (
              <div className="mt-2">
                <a
                  href={selectedRecord.upload_authorization}
                  download={selectedRecord.upload_authorization_file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedRecord.upload_authorization_file_name}
                </a>
              </div>
            )}
          </div>

          <div className="col-sm-4">
            <h6 className="my-6">Check Request</h6>
            {selectedRecord?.check_request_document && (
              <div className="mt-2">
                <a
                  href={selectedRecord.check_request_document}
                  download={selectedRecord.check_request_file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedRecord.check_request_file_name}
                </a>
              </div>
            )}
          </div>

          <div className="col-sm-4">
            <h6 className="my-6">Other Documents</h6>
            {selectedRecord?.other_document && (
              <div className="mt-2">
                <div className="d-flex items-center gap-2">
                  <a
                    href={
                      selectedRecord?.other_document instanceof File
                        ? URL.createObjectURL(selectedRecord?.other_document)
                        : selectedRecord?.other_document
                    }
                    download
                    className="text-blue-500 cursor-pointer flex items-center gap-1"
                  >
                    <DownloadSimple size={25} />
                    {selectedRecord.other_document_file_name}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <hr className="mt-4" />

        <h4 className="my-4">Payment Info</h4>
        <div className="row">
          <div className="col-sm-4">
            <h6 className="my-6">Date of Check</h6>
            <p className="heading-sm">
              {selectedRecord?.date_of_check
                ? formatDate(selectedRecord.date_of_check)
                : ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">HP Submission Reimbursement Date</h6>
            <p className="heading-sm">
              {selectedRecord?.hp_submission_reimbursement_date
                ? formatDate(selectedRecord.hp_submission_reimbursement_date)
                : ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">HP Payment Date</h6>
            <p className="heading-sm">
              {selectedRecord?.hp_payment_date
                ? formatDate(selectedRecord.hp_payment_date)
                : ""}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">Total Expense</h6>
            <p className="heading-sm">
              $
              {selectedRecord?.expense_informations
                ?.reduce(
                  (sum, info) => sum + parseFloat(info.expense_amount || 0),
                  0,
                )
                .toFixed(2) || "0.00"}
            </p>
          </div>
          <div className="col-sm-4">
            <h6 className="my-6">Receipt</h6>
            {selectedRecord?.upload_receipt && (
              <div className="mt-2">
                <a
                  href={selectedRecord.upload_receipt}
                  download={selectedRecord.upload_receipt_file_name}
                  className="text-blue-500 cursor-pointer flex items-center gap-1"
                >
                  <DownloadSimple size={25} />
                  {selectedRecord.upload_receipt_file_name}
                </a>
              </div>
            )}
          </div>
        </div>

        <hr className="mt-4" />
        <div className="row">
          <div className="col-sm-12">
            <h6 className="my-6">Comment</h6>
            <p className="heading-sm">{selectedRecord?.comment || ""}</p>
          </div>
        </div>
        <hr className="mt-4" />
        <ApprovalStepper housingExpense={selectedRecord} />
        <button
          className="secondary-btn w-100 mt-4"
          onClick={handleCloseViewRecord}
        >
          Close
        </button>
      </CustomModal>
      {FilterHousingExpenseRequests && (
        <CustomModal
          size="sm"
          show
          onHide={handleCloseFilterHousingExpenseRequest}
          heading={`Filter Housing Expense`}
        >
          <HousingExpenseRequestFilter
            handleSubmittion={handleApplyFilters}
            handleClose={handleCloseFilterHousingExpenseRequest}
            currentFilters={filters}
          />
        </CustomModal>
      )}
      <CustomModal
        show={isRejectedPaymentVisible}
        onHide={handleCloseRejectPayment}
        size="sm"
        heading={"Deny Expense Request"}
      >
        <PaymentRejectForm
          onCancel={handleCloseRejectPayment}
          handleSubmission={RejectHousingPaymentSubmission}
          selectedId={selectedId?.id}
        />
      </CustomModal>
      <CustomModal
        show={isAcceptPaymentVisible}
        onHide={handleCloseAcceptPayment}
        size="sm"
        heading={
          selectedId?.status === "approved" ? (
            "Confirm Payment Issuance"
          ) : (
            <span>
              Approve housing expense request for
              <br />
              {`${selectedId?.member?.first_name} ${selectedId?.member?.last_name}`}
            </span>
          )
        }
      >
        {selectedId?.status === "approved" ? (
          <PaymentForm
            onClose={handleCloseAcceptPayment}
            handleSubmission={ApprovedHousingPaymentSubmission}
            selectedId={selectedId?.id}
          />
        ) : (
          <HousingPaymentForm
            onClose={handleCloseAcceptPayment}
            handleSubmission={ApprovedHousingPaymentSubmission}
            selectedId={selectedId?.id}
          />
        )}
      </CustomModal>
      <CustomModal
        show={isExpenseModalOpen}
        onHide={handleExpenseModalClose}
        size="lg"
        heading={"Create Expense Request"}
      >
        <AddExpenseWithMember
          selectedExpense={selectedExpense}
          setSelectedExpense={setSelectedExpense}
          // handleOpenAddNewMemberModal={handleOpenAddNewMemberModal}
          handleExpenseModalClose={handleExpenseModalClose}
          handleRefresh={handleRefresh}
        />
      </CustomModal>
      {/* {addNewMemberModal && (
        <CustomModal
          size="sm"
          show
          onHide={handleCloseAddNewMemberModal}
          heading={`Add Member`}
        >
          <AddFormWithoutMember
            handleSubmitForm={""}
            handleClose={handleCloseAddNewMemberModal}
          />
        </CustomModal>
      )} */}
      <CustomModal
        show={isEditModalVisible}
        onHide={() => {
          setIsEditModalVisible(false);
          setEditRecord(null);
          handleRefresh();
        }}
        size="md"
        heading={"Edit Housing Expense Request"}
      >
        {isEditModalVisible && editRecord && (
          <CreateHousingExpenseForm
            selectedMember={editRecord?.member || {}}
            expenseRecord={editRecord}
            onClose={() => {
              setIsEditModalVisible(false);
              setEditRecord(null);
              handleRefresh();
            }}
          />
        )}
      </CustomModal>
    </>
  );
};

export default HousingExpenseRequestComp;
